import Category from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import {
  IFlowForDisplay,
  IGroupedFlowsForDisplay
} from '@Client/runner.services/flows.formatter';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import BoxFilter, { BoxFilterColor } from '@Client/runner.shared/BoxFilter';
import FlowGroups from '@Client/runner.shared/FlowGroups/FlowGroups';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import KendoReactDatePicker from '@Shared.Angular/kendo.react/KendoReactDatePicker';
import React, { useEffect, useState } from 'react';

const FlowsImIn = () => {
  const browserUtilsService = getService('browserUtilsService');
  const $state = getService('$state');
  const flowImInService = getService('flowImInService');
  const flowinglyMomentService = getService('flowinglyMomentService');
  const momentService = getService('momentService');
  const pubsubService = getService('pubsubService');
  const APP_CONFIG = getService('APP_CONFIG');

  const allCategoriesId = EmptyGuid;
  const allFlowModelsId = EmptyGuid;
  const isMobile = browserUtilsService.isMobileDevice();

  const savedFilters = flowImInService.getSavedFilters();

  const [selectedCategoryId, setSelectedCategoryId] = useState<Guid>(
    savedFilters.categoryId
  );
  const [selectedTabId, setSelectedTabId] = useState<FlowsImInTabId>(
    savedFilters.tab
  );
  const [fromDate, setFromDate] = useState<Date>(savedFilters.fromDate);
  const [toDate, setToDate] = useState<Date>(savedFilters.toDate);
  const [selectedTagIds, setSelectedTagIds] = useState<Guid[]>(
    savedFilters.tagIds
  );
  const [selectedFlowModelId, setSelectedFlowModelId] = useState<Guid>(
    savedFilters.flowModelId
  );
  const [selectedBoxFilterId, setSelectedBoxFilterId] =
    useState<FlowsImInBoxFilters>(savedFilters.boxFilterId);

  const [allGroupedFlows, setAllGroupedFlows] = useState<
    IGroupedFlowsForDisplay[]
  >([]);
  const [displayedGroupedFlows, setDisplayedGroupedFlows] = useState<
    IGroupedFlowsForDisplay[]
  >([]);
  const [allFlowModels, setAllFlowModels] = useState<
    { id: Guid; name: string }[]
  >([]);
  const [categories, setCategories] = useState<
    ITreeCategory<IPublishedFlowModel>[]
  >([]);
  const [dataIsReady, setDataIsReady] = useState(false);

  const [allFlowModelTagIds, setAllFlowModelTagIds] = useState<Guid[]>([]);
  const [boxFilterAllCount, setBoxFilterAllCount] = useState(0);
  const [boxFilterInProgressCount, setBoxFilterInProgressCount] = useState(0);
  const [boxFilterDueSoonCount, setBoxFilterDueSoonCount] = useState(0);
  const [boxFilterDueTodayCount, setBoxFilterDueTodayCount] = useState(0);
  const [boxFilterOverdueCount, setBoxFilterOverdueCount] = useState(0);
  const [boxFilterCompletedCount, setBoxFilterCompletedCount] = useState(0);
  const [boxFilterRejectedCount, setBoxFilterRejectedCount] = useState(0);
  const [boxFilterCancelledCount, setBoxFilterCancelledCount] = useState(0);

  const canStartFlows = getService(
    'permissionsService'
  ).currentUserHasPermission(FlowinglyPermissions.FLOW_START);
  const emptyPageTitle = 'Looks like there is nothing here';
  const emptyPageText =
    'There are no Flows you are involved in that match your filter criteria.' +
    (canStartFlows
      ? "<br />To start a Flow, go to the 'Start' page, or click the button below."
      : '');
  const emptyPageButtonLabel = canStartFlows ? 'Start a Flow' : undefined;

  const tabs: ICustomTab[] = [
    {
      id: FlowsImInTabId.STARTED_BY_ME,
      label: 'Started by me'
    },
    {
      id: FlowsImInTabId.ALL,
      label: 'All flows'
    }
  ];

  const selectBoxFilter = (boxFilterId: FlowsImInBoxFilters) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      boxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );
    setSelectedBoxFilterId(boxFilterId);
  };

  const boxFilters = [
    {
      id: FlowsImInBoxFilters.ALL,
      title: 'All',
      toolTip: 'All flows waiting on you',
      count: boxFilterAllCount,
      color: BoxFilterColor.TEAL,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.ALL
    },
    {
      id: FlowsImInBoxFilters.IN_PROGRESS,
      title: 'In progress',
      toolTip: 'All flows that are in progress',
      count: boxFilterInProgressCount,
      color: BoxFilterColor.TEAL,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.IN_PROGRESS
    },
    {
      id: FlowsImInBoxFilters.DUE_SOON,
      title: 'Due soon',
      toolTip: 'Flows waiting on you that are due soon',
      count: boxFilterDueSoonCount,
      color: BoxFilterColor.BLACK,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.DUE_SOON
    },
    {
      id: FlowsImInBoxFilters.DUE_TODAY,
      title: 'Due today',
      toolTip: 'Flows waiting on you that are due today',
      count: boxFilterDueTodayCount,
      color: BoxFilterColor.BLACK,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.DUE_TODAY
    },
    {
      id: FlowsImInBoxFilters.OVERDUE,
      title: 'Overdue',
      toolTip: 'Flows waiting on you that are overdue',
      count: boxFilterOverdueCount,
      color: BoxFilterColor.ORANGE,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.OVERDUE
    },
    {
      id: FlowsImInBoxFilters.COMPLETED,
      title: 'Completed',
      toolTip: 'Flows that are completed',
      count: boxFilterCompletedCount,
      color: BoxFilterColor.GREEN,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.COMPLETED
    },
    {
      id: FlowsImInBoxFilters.REJECTED,
      title: 'Rejected',
      toolTip: 'Flows that are rejected',
      count: boxFilterRejectedCount,
      color: BoxFilterColor.ORANGE,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.REJECTED
    }
  ];
  if (APP_CONFIG.enableCancelledFlowsView) {
    boxFilters.push({
      id: FlowsImInBoxFilters.CANCELLED,
      title: 'Cancelled',
      toolTip: 'Flows that are cancelled',
      count: boxFilterCancelledCount,
      color: BoxFilterColor.MANDARIN,
      onClick: selectBoxFilter,
      selected: selectedBoxFilterId === FlowsImInBoxFilters.CANCELLED
    });
  }

  const flowStatusFilters = [
    { id: FlowsImInBoxFilters.IN_PROGRESS, name: 'In Progress' },
    { id: FlowsImInBoxFilters.ALL, name: 'All' },
    { id: FlowsImInBoxFilters.COMPLETED, name: 'Completed' },
    { id: FlowsImInBoxFilters.REJECTED, name: 'Rejected' },
    { id: FlowsImInBoxFilters.CANCELLED, name: 'Cancelled' }
  ];

  const getFlows = () => {
    return flowImInService
      .getFlows(selectedTabId, fromDate, toDate)
      .then((groupedFlows) => {
        setNewGroupedFlows(groupedFlows);
      });
  };

  useEffect(() => {
    getFlows();
  }, [selectedTabId, fromDate, toDate]);

  const subscriberId = 'flowingly.runner.flows.imin';
  pubsubService.unsubscribeAll(subscriberId);
  pubsubService.subscribeManyLate(
    [
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      'SIGNALR_RUNNER_STEP_WEBHOOK_UPDATED',
      'SIGNALR_RUNNER_START_FLOW',
      'SIGNALR_RUNNER_COMPLETE_STEP',
      'SIGNALR_RUNNER_REASSIGN_STEP',
      'SIGNALR_RUNNER_WITHDRAW_FLOW',
      'SIGNALR_ACTOR_DELETED',
      'SIGNALR_RUNNER_USER_TEAM_UPDATED',
      'SIGNALR_RUNNER_NEW_FLOW_COMMENT_COUNT',
      'SIGNALR_RUNNER_STEP_TASK_CANCELLED',
      'SIGNALR_RUNNER_STEP_TASK_CREATED',
      'SIGNALR_RUNNER_STEP_TASK_UPDATED',
      'SIGNALR_RUNNER_STEP_INTEGRATION_PROCESSING_UPDATED',
      'WORKFLOW_DELETED'
    ],
    getFlows,
    subscriberId
  );

  useEffect(() => {
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const saveFilters = () => {
    flowImInService.saveFilters(
      selectedCategoryId,
      selectedTabId,
      fromDate,
      toDate,
      selectedTagIds,
      selectedFlowModelId,
      selectedBoxFilterId
    );
  };
  useEffect(() => {
    return () => {
      saveFilters();
    };
  }, [saveFilters]);

  const setNewGroupedFlows = (newGroups: IGroupedFlowsForDisplay[]) => {
    setAllGroupedFlows(newGroups);
    updateDisplayedFlowModels(
      newGroups,
      selectedCategoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );

    const newTagIds = new Set<Guid>();
    newGroups.forEach((group) => {
      group.flows.forEach((flow) => {
        flow.parsedTags.forEach((tag) => newTagIds.add(tag.id));
      });
    });
    setAllFlowModelTagIds(Array.from(newTagIds));
    const validTagIds = selectedTagIds.filter((id) => newTagIds.has(id));
    setSelectedTagIds(validTagIds);

    const newCategories = generateCategories(newGroups);
    setCategories(newCategories);
    const findSelectedCategory = (current: ITreeCategory<unknown>) => {
      if (current.id === selectedCategoryId) {
        return true;
      }
      return current.items.some(findSelectedCategory);
    };
    if (!newCategories.some(findSelectedCategory)) {
      setSelectedCategoryId(allCategoriesId);
    }

    setDataIsReady(true);
  };

  const isFlowDueSoon = (dueDate: string) => {
    return dueDate && !flowinglyMomentService.isOverdue(dueDate);
  };

  const isFlowDueToday = (dueDate: string, todayDate: string) => {
    return (
      dueDate &&
      !flowinglyMomentService.isOverdue(dueDate) &&
      momentService
        .utc(dueDate, 'YYYY-MM-DD HH:mm:ss')
        .local()
        .format('YYYY-MM-DD') === todayDate
    );
  };

  const isFlowOverdue = (dueDate: string) => {
    return flowinglyMomentService.isOverdue(dueDate);
  };

  const isFlowCompleted = (flow: IFlowForDisplay) => {
    return flow.finalisedDate && flow.finalisedReason === 'Completed';
  };

  const isFlowInProgress = (finalisedDate: string) => {
    return !finalisedDate;
  };

  const isFlowRejected = (flow: IFlowForDisplay) => {
    return flow.finalisedDate && flow.finalisedReason === 'Rejected';
  };

  const isFlowCancelled = (flow: IFlowForDisplay) => {
    return flow.finalisedDate && flow.finalisedReason === 'Withdrawn';
  };

  const generateCategories = (groupedFlows: IGroupedFlowsForDisplay[]) => {
    const categories = [] as ITreeCategory<IPublishedFlowModel>[];
    for (const flowModel of groupedFlows) {
      let categoryTree = flowModel.categoryTree;
      let currentCategories = categories;
      while (categoryTree) {
        let category = currentCategories.find(
          (cat) => cat.id === categoryTree.categoryId
        );
        if (!category) {
          category = {
            id: categoryTree.categoryId,
            text: categoryTree.categoryName,
            flowModels: [],
            items: [],
            count: 0
          };
          currentCategories.push(category);
        }
        currentCategories = category.items;
        categoryTree = categoryTree.category;
        if (!categoryTree) {
          category.count += flowModel.flows.length;
        }
      }
    }

    categories.sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );

    categories.splice(0, 0, {
      id: allCategoriesId,
      text: 'All',
      flowModels: [],
      items: []
    });

    return categories;
  };

  const selectCategory = (categoryId: Guid) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      categoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      selectedTagIds
    );
    setSelectedCategoryId(categoryId);
  };

  const selectTagFilters = (tagIds: Guid[]) => {
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      selectedBoxFilterId,
      selectedFlowModelId,
      tagIds
    );
    setSelectedTagIds(tagIds);
  };

  const selectFlowModelFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFlowModelId = e.target.value as Guid;
    updateDisplayedFlowModels(
      allGroupedFlows,
      selectedCategoryId,
      selectedBoxFilterId,
      newFlowModelId,
      selectedTagIds
    );
    setSelectedFlowModelId(newFlowModelId);
  };

  const updateDisplayedFlowModels = (
    groups: IGroupedFlowsForDisplay[],
    categoryId: Guid,
    boxFilterId: FlowsImInBoxFilters,
    flowModelId: Guid,
    filterTagIds: Guid[]
  ) => {
    let filteredGroups = groups;
    filteredGroups.forEach((group) => {
      group.flows.forEach((flow) => {
        flow.show = true;
      });
    });
    if (categoryId !== allCategoriesId) {
      filteredGroups = filteredGroups.filter(
        (group) => group.categoryId === categoryId
      );
    }

    const newFlowModels = [
      { id: allFlowModelsId, name: 'All Flows' },
      ...filteredGroups.map((g) => {
        return { id: g.flowModelId, name: g.groupName };
      })
    ];
    setAllFlowModels(newFlowModels);
    if (!newFlowModels.some((m) => m.id === selectedFlowModelId)) {
      setSelectedFlowModelId(allFlowModelsId);
      flowModelId = allFlowModelsId;
    }

    if (filterTagIds.length > 0) {
      filteredGroups = filteredGroups.filter((group) => {
        let groupIncludesTag = false;
        group.flows.forEach((flow) => {
          flow.show = flow.parsedTags.some((flowTag) => {
            if (filterTagIds.includes(flowTag.id)) {
              groupIncludesTag = true;
              return true;
            }
            return false;
          });
        });
        return groupIncludesTag;
      });
    }

    if (flowModelId !== allFlowModelsId) {
      filteredGroups = filteredGroups.filter(
        (group) => group.flowModelId === flowModelId
      );
    }

    setBoxFilterAllCount(
      filteredGroups.reduce(
        (acc, group) => acc + group.flows.filter((f) => f.show).length,
        0
      )
    );

    const inProgressCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter(
          (flow) => flow.show && isFlowInProgress(flow.finalisedDate)
        ).length,
      0
    );
    setBoxFilterInProgressCount(inProgressCount);

    const dueSoonCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowDueSoon(flow.dueDate))
          .length,
      0
    );
    setBoxFilterDueSoonCount(dueSoonCount);

    const todayDateString = momentService(new Date())
      .local()
      .format('YYYY-MM-DD');
    const dueTodayCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter(
          (flow) => flow.show && isFlowDueToday(flow.dueDate, todayDateString)
        ).length,
      0
    );
    setBoxFilterDueTodayCount(dueTodayCount);

    const overdueCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowOverdue(flow.dueDate))
          .length,
      0
    );
    setBoxFilterOverdueCount(overdueCount);

    const completedCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowCompleted(flow)).length,
      0
    );
    setBoxFilterCompletedCount(completedCount);

    const rejectedCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowRejected(flow)).length,
      0
    );
    setBoxFilterRejectedCount(rejectedCount);

    const cancelledCount = filteredGroups.reduce(
      (acc, group) =>
        acc +
        group.flows.filter((flow) => flow.show && isFlowCancelled(flow)).length,
      0
    );
    setBoxFilterCancelledCount(cancelledCount);

    if (boxFilterId === FlowsImInBoxFilters.ALL) {
      // Don't filter
    } else if (boxFilterId === FlowsImInBoxFilters.IN_PROGRESS) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowInProgress(flow.finalisedDate);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.DUE_SOON) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowDueSoon(flow.dueDate);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.DUE_TODAY) {
      const todayDateString = momentService(new Date())
        .local()
        .format('YYYY-MM-DD');
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show =
            flow.show && isFlowDueToday(flow.dueDate, todayDateString);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.OVERDUE) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowOverdue(flow.dueDate);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.COMPLETED) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowCompleted(flow);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.REJECTED) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowRejected(flow);
        }
      }
    } else if (boxFilterId === FlowsImInBoxFilters.CANCELLED) {
      for (const group of filteredGroups) {
        for (const flow of group.flows) {
          flow.show = flow.show && isFlowCancelled(flow);
        }
      }
    }

    filteredGroups = filteredGroups.filter((group) =>
      group.flows.some((flow) => flow.show)
    );

    setDisplayedGroupedFlows(filteredGroups);
  };

  const notificationService = getService('notificationService');
  const checkDatesAreValid = (fromDate: Date, toDate: Date) => {
    if (!fromDate) {
      notificationService.showErrorToast('From date is required');
      return false;
    }
    if (!toDate) {
      notificationService.showErrorToast('To date is required');
      return false;
    }
    if (fromDate > toDate) {
      notificationService.showErrorToast('From date must be before to date');
      return false;
    }
    if (toDate > momentService().endOf('day').toDate()) {
      notificationService.showErrorToast('To date cannot be in the future');
      return false;
    }
    const monthDiffAllowed = APP_CONFIG.flowsInDateFilterMaxRange;
    const maxEndDate = momentService(fromDate)
      .add(monthDiffAllowed, 'months')
      .toDate();
    if (toDate > maxEndDate) {
      notificationService.showErrorToast(
        `Date range cannot be more than ${monthDiffAllowed} months`
      );
      return false;
    }
    return true;
  };

  return (
    <div id="flows-imin" className={isMobile ? 'mobile' : 'runner-page'}>
      <RunnerPageDescription
        showText={!isMobile}
        pageHeaderText="Flows I'm In"
        pageDescription="A list of all flows started by you."
        helpLink="https://help.flowingly.net/frequently-asked-questions/how-do-i-participate-in-or-view-flows-i-started-or-am-involved-in"
      />
      {dataIsReady && (
        <div className="flows-imin-content runner-page__content">
          {!isMobile && (
            <Category
              isMobile={isMobile}
              categorisedItems={categories}
              selectedCategoryId={selectedCategoryId}
              onCategoryClick={selectCategory}
              defaultItemId={allCategoriesId}
            />
          )}
          <div className="flows-imin-list runner-page__content__main-wrapper">
            <div className="runner-page__content__main-content">
              <div className="header">
                <CustomTabs
                  tabItems={tabs}
                  selectedTabId={selectedTabId}
                  onChange={(newTabId) =>
                    setSelectedTabId(newTabId as FlowsImInTabId)
                  }
                />
                <div className="toolbar">
                  {APP_CONFIG.showDashboardsV1 && !isMobile && (
                    <div className="flows-imin-date-pickers">
                      <i
                        className="fa-light fa-info-circle"
                        title="Filter by flow start date"
                      ></i>
                      <KendoReactDatePicker
                        format={'dd/MM/yyyy'}
                        value={fromDate}
                        max={momentService().endOf('day').toDate()}
                        onChange={(event) => {
                          if (checkDatesAreValid(event.value, toDate)) {
                            setFromDate(event.value);
                          }
                        }}
                      />
                      <div className="flows-imin-date-pickers__to-date">
                        <span className="date-spacer"> To </span>
                        <KendoReactDatePicker
                          format={'dd/MM/yyyy'}
                          value={toDate}
                          max={momentService().endOf('day').toDate()}
                          onChange={(event) => {
                            if (checkDatesAreValid(fromDate, event.value)) {
                              setToDate(event.value);
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flows-imin-list-dropdowns">
                    {APP_CONFIG.enableTagging && !isMobile && (
                      <TagFilter
                        usedTagIds={allFlowModelTagIds}
                        initialSelectedTagIds={selectedTagIds}
                        onApply={selectTagFilters}
                        popoverAlign={
                          APP_CONFIG.showDashboardsV1 ? 'right' : 'left'
                        }
                      ></TagFilter>
                    )}
                    <select
                      id="flows-imin-flow-model-select"
                      data-testid="flows-imin-flow-model-filter"
                      title="Filter by Flow Model"
                      onChange={selectFlowModelFilter}
                      value={selectedFlowModelId}
                    >
                      {allFlowModels.map((model) => (
                        <option
                          key={model.id}
                          value={model.id}
                          title={model.name}
                        >
                          {model.name.substring(
                            0,
                            APP_CONFIG.flowFilterMaxCharacters
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!APP_CONFIG.showDashboardsV1 && !isMobile && (
                    <select
                      id="flows-imin-flow-status-select"
                      data-testid="flows-imin-flow-status-filter"
                      title="Filter by Flow Status"
                      onChange={(e) => {
                        const boxFilterId = parseInt(e.target.value);
                        selectBoxFilter(boxFilterId);
                      }}
                      value={selectedBoxFilterId}
                    >
                      {flowStatusFilters.map((filter) => (
                        <option key={filter.id} value={filter.id}>
                          {filter.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {APP_CONFIG.showDashboardsV1 && !isMobile && (
                <div
                  className="toolbar box-filters-toolbar"
                  data-testid="flows-imin-box-filters"
                >
                  <div className="box-filters">
                    {boxFilters.map((filter) => (
                      <BoxFilter
                        key={filter.id}
                        title={filter.title}
                        tooltip={filter.toolTip}
                        count={filter.count}
                        color={filter.color}
                        onClick={() => filter.onClick(filter.id)}
                        selected={filter.selected}
                        testId={`flows-imin-box-filter-${filter.id}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              <FlowGroups
                isMobile={isMobile}
                groups={displayedGroupedFlows}
                onFlowClick={(flowId: Guid) => {
                  $state.go(STATE_MAPPINGS.flow, { flowId });
                }}
              ></FlowGroups>
              {displayedGroupedFlows.length === 0 && (
                <EmptyState
                  summaryText={emptyPageTitle}
                  bodyText={emptyPageText}
                  buttonText={emptyPageButtonLabel}
                  onButtonClick={() => $state.go(STATE_MAPPINGS.flowsactive)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsImIn;

export enum FlowsImInBoxFilters {
  ALL = 0,
  DUE_SOON = 1,
  DUE_TODAY = 2,
  OVERDUE = 3,
  IN_PROGRESS = 4,
  COMPLETED = 5,
  REJECTED = 6,
  CANCELLED = 7
}

export enum FlowsImInTabId {
  STARTED_BY_ME = 'startedByMe',
  ALL = 'all'
}
